/* eslint-disable react-hooks/globals */
import {
  Dispatch,
  SerializedError,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";
import {
  FetchBaseQueryError,
  QueryStatus,
  TypedUseQueryHookResult,
} from "@reduxjs/toolkit/query/react";
import { createMappingInstance } from "@timeedit/registration-components";
import { ITEToken, SCOPE, TCurrentUser } from "@timeedit/registration-schemas";
import { Mapping, isDefined } from "@timeedit/registration-shared";
import { useMemo, useState } from "react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import {
  useFetchMappingStoreQuery,
  useFetchTeUserQuery,
  useLazyFetchTeUserQuery,
} from "./api/te";
import { baseQuery } from "./baseQuery";
import { AppDispatch, RootState } from "./store";

type UseUserDataType = TypedUseQueryHookResult<
  TCurrentUser & ITEToken,
  ITEToken | undefined,
  typeof baseQuery
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any,
const noRefetch: any = (): void => {};

export function useUserData(): UseUserDataType {
  const { loginData } = useAppSelector((state) => state.login);

  const queryProps = loginData ?? ({} as ITEToken);
  const rootData = rootDataWithRefetch(queryProps);
  const response = useFetchTeUserQuery(queryProps, {
    skip: loginData === undefined && rootData !== undefined,
  });
  return rootData ?? isDefined(response.data)
    ? response
    : {
        isError: false,
        isLoading: true,
        data: undefined,
        currentData: undefined,
        fulfilledTimeStamp: new Date().getTime(),
        error: undefined,
        isFetching: true,
        isSuccess: false,
        isUninitialized: false,
        status: QueryStatus.pending,
        refetch: noRefetch,
      };

  function rootDataWithRefetch(data: ITEToken): UseUserDataType | undefined {
    const rootUserData = getRootUserData(data);
    return rootUserData ? { ...rootUserData, refetch: noRefetch } : undefined;
  }
}

// RTK query function, makes it hard to type properly
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useLazyUserData() {
  const { loginData } = useAppSelector((state) => state.login);

  const [triggerFetchUserData, useFetchTeUserResponse] =
    useLazyFetchTeUserQuery();

  const initialData = {
    isError: false,
    isLoading: true,
    data: undefined,
    currentData: undefined,
    fulfilledTimeStamp: 0,
    error: undefined,
    isFetching: false,
    isSuccess: false,
    isUninitialized: false,
    status: QueryStatus.pending,
    refetch: noRefetch,
  } satisfies UseUserDataType;

  const [userResponse, setUserResponse] =
    useState<UseUserDataType>(initialData);

  const triggerUserData = (params?: {
    token?: ITEToken;
    preferCacheValue?: boolean;
  }) => {
    const { token, preferCacheValue } = params ?? {};
    if (token !== undefined) {
      const rootUserData = getRootUserData(token);
      if (rootUserData !== undefined) {
        setUserResponse(() => rootUserData);
        return undefined;
      }
    }

    if (loginData !== undefined) {
      return triggerFetchUserData(loginData, preferCacheValue);
    }
    return undefined;
  };

  return [triggerUserData, useFetchTeUserResponse ?? userResponse] as const;
}

function getRootUserData(props: ITEToken): UseUserDataType | undefined {
  // Root users are not stored as a user in the database, so we just want to use the data from the login token in that case (which is stored in rootUserData).
  if (props && props.scopes && props.scopes.includes(SCOPE.ROOT)) {
    return {
      isError: false,
      isLoading: false,
      data: { ...props },
      currentData: { ...props },
      fulfilledTimeStamp: new Date().getTime(),
      error: undefined,
      isFetching: false,
      isSuccess: true,
      isUninitialized: false,
      status: QueryStatus.fulfilled,
      refetch: noRefetch,
    } satisfies UseUserDataType;
  }
  return undefined;
}

let mapping: Mapping | null = null;

type UseMappingInitTrueResult = {
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  error: (FetchBaseQueryError | SerializedError | undefined)[];
};

/**
 * Mapping hook to get the mapping instance inside React components.
 * Use with `init: true` to initialize the mapping instance in a
 * central component, and block rendering until initialization is
 * done. After that, use without argument to conveniently get the
 * mapping instance directly.
 * @param init - Whether to initialize the mapping instance
 * @returns Mapping instance or fetch result
 */
export function useMapping(props: { init: true }): UseMappingInitTrueResult;
export function useMapping(props?: { init: false }): Mapping;
export function useMapping({ init } = { init: false }): unknown {
  const mappingStore = useFetchMappingStoreQuery(undefined, {
    skip: !init || !!mapping,
  });

  const memoizedMapping = useMemo(() => {
    if (!mapping && mappingStore.isSuccess) {
      mapping = createMappingInstance(mappingStore.data);
    }
    return mapping;
  }, [mappingStore.isSuccess, mappingStore.data]);

  return init
    ? {
        isLoading: mappingStore.isLoading,
        isFetching: mappingStore.isFetching,
        isError: mappingStore.isError,
        error: [mappingStore.error],
      }
    : memoizedMapping;
}

type UseAppDispatchReturn = () => ThunkDispatch<
  RootState,
  undefined,
  UnknownAction
> &
  Dispatch<UnknownAction>;
export const useAppDispatch: UseAppDispatchReturn = () =>
  useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
